import {useEffect, useRef, useState} from 'react';
import {view} from '@sail/ui';
import type {MessagePartComponentProps} from '../MessagePartComponentContext';
import {MarkdownText} from '../../StripeAssistant/LiveAnswersV2/MarkdownText';
import {
  InlineLinkClickHandler,
  InlineLinkProps,
} from '../../StripeAssistant/LiveAnswersV2/InlineLinkClickHandler';
import {StreamingBuffer} from '../../lib/StreamingBuffer';
import {useAnalytics} from '../../AnalyticsProvider';
import {useConversationTypewriting} from '../ConversationActivityIndicatorProvider';
import {LoadingTextAsync} from '../LoadingTextAsync';

const BUFFER_CHUNK_SIZE = 5;
const BUFFER_FLUSH_INTERVAL_MS = 20;

export function AiResponsePartComponent({
  message,
  part,
  isLive,
}: MessagePartComponentProps) {
  const isStreaming =
    part.dynamic === 'if_latest' ||
    part.dynamic === 'active' ||
    message.data?.dynamic === 'if_latest' ||
    message.data?.dynamic === 'active';
  const sourceText = part.content.text || '';

  // Skip the streaming buffer when:
  // 1. Message existed at initial render (historical, not isLive)
  // 2. Message arrived in a hidden tab and is already complete (another tab streamed it)
  const skipBufferRef = useRef(
    (!isLive && !isStreaming && sourceText.length > 0) ||
      (isLive && !isStreaming && sourceText.length > 0 && document.hidden),
  );

  const [displayText, setDisplayText] = useState(() =>
    skipBufferRef.current ? sourceText : '',
  );
  const [bufferComplete, setBufferComplete] = useState(skipBufferRef.current);
  const bufferRef = useRef<StreamingBuffer | null>(null);
  const analytics = useAnalytics();
  const {registerTypewriting} = useConversationTypewriting();
  const messageData = message.data as {
    conversationsApi: {messageId: string};
  };
  const sourceMessageId = messageData.conversationsApi.messageId;

  useEffect(() => {
    if (skipBufferRef.current) {
      return;
    }
    // A live buffer is about to flush — keep the input pending until it
    // finishes typewriting.
    const finishTypewriting = registerTypewriting(sourceMessageId);
    const buffer = new StreamingBuffer({
      chunkSize: BUFFER_CHUNK_SIZE,
      flushIntervalMs: BUFFER_FLUSH_INTERVAL_MS,
      onFlush: (content: string) => setDisplayText(content),
      onComplete: () => {
        setBufferComplete(true);
        finishTypewriting();
      },
    });
    bufferRef.current = buffer;
    return () => {
      buffer.cancel();
      finishTypewriting();
    };
  }, [registerTypewriting, sourceMessageId]);

  useEffect(() => {
    if (skipBufferRef.current) {
      return;
    }

    const buffer = bufferRef.current;
    if (!buffer) {
      return;
    }

    const newContent = sourceText.slice(buffer.length);
    if (newContent.length > 0) {
      buffer.push(newContent);
    }

    if (!isStreaming && buffer.status !== 'complete') {
      if (document.hidden) {
        buffer.flushAll();
      } else {
        buffer.complete();
      }
    }
  }, [sourceText, isStreaming]);

  useEffect(() => {
    if (skipBufferRef.current) {
      return;
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const buffer = bufferRef.current;
        if (buffer && buffer.pendingChunks.length > 0 && !isStreaming) {
          buffer.flushAll();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isStreaming]);

  if (!displayText && bufferComplete) {
    return null;
  }

  return (
    <view.div
      data-testid={displayText ? 'ai-response-part' : 'ai-response-loading'}
      data-message-id={message.id}
      css={{stack: 'y', gap: 'xsmall'}}
    >
      {!displayText ? <LoadingTextAsync stage="just_one_moment" /> : null}
      {displayText ? (
        <InlineLinkClickHandler
          onClick={({href, innerText, parentInnerText}: InlineLinkProps) => {
            analytics.action(
              'universal_help_live_answers_frontend_inline_link_clicked',
              {
                live_answers_source_url: href,
                live_answers_source_title: innerText,
                live_answers_source_context: parentInnerText,
              },
            );
          }}
        >
          <MarkdownText body={displayText} />
        </InlineLinkClickHandler>
      ) : null}
    </view.div>
  );
}
